import { isTauri, isIOS, isAndroid, isMacOS } from './shell/shell.js';
import type { ClientErrorReport } from './auth/apiClient.js';

// UX-001: client-side crash forensics. Until this shipped, a render throw or unhandled rejection took
// down the shell recorded NOWHERE — on an auto-updating desktop/mobile fleet, an undiagnosable white
// screen. This module always logs a structured console line and (once wired) best-effort ships the
// report to /client-errors so it lands in Cloud Logging and rides the OBS-001 alert. No third-party SDK.

/** App version, inlined at build time from package.json (see vite.config.ts `define`). */
export const APP_VERSION: string =
  typeof __APP_VERSION__ === 'string' && __APP_VERSION__ ? __APP_VERSION__ : 'dev';

/** Coarse platform tag so a crash can be triaged to a client (web vs. the auto-updating shells). */
export function platformTag(): string {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  if (isTauri()) return isMacOS() ? 'desktop-macos' : 'desktop';
  return 'web';
}

type Sink = (report: ClientErrorReport) => Promise<unknown>;
let sink: Sink | null = null;

/**
 * Wire the authenticated transport (apiClient.postClientError) once the app has an api client. Until
 * this is set — or after it's cleared on sign-out — reports still hit the console; they just aren't
 * shipped (a crash on the signed-out login screen has no token to authenticate the POST anyway).
 */
export function setClientErrorSink(fn: Sink | null): void {
  sink = fn;
}

// Bound the blast radius of a crash LOOP: a component that throws on every render, or a rejection
// storm, must not flood the log sink. Cap total reports per session and collapse exact duplicates.
const MAX_REPORTS_PER_SESSION = 25;
let reportCount = 0;
const seen = new Set<string>();

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s;
}

/**
 * Record a client-side failure: always logs a structured console line (visible in desktop devtools /
 * the browser console) and best-effort POSTs it to /client-errors. NEVER throws — error reporting must
 * not itself become an error inside an ErrorBoundary or a window handler.
 */
export function reportClientError(
  kind: ClientErrorReport['kind'],
  error: unknown,
  source?: string,
): void {
  try {
    const err = error instanceof Error ? error : undefined;
    const message = truncate(err?.message ?? String(error ?? 'unknown error'), 2000);
    const stack = err?.stack ? truncate(err.stack, 12_000) : undefined;

    const dedupeKey = `${kind}:${message}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    if (reportCount >= MAX_REPORTS_PER_SESSION) return;
    reportCount += 1;

    const report: ClientErrorReport = {
      kind,
      message,
      ...(stack ? { stack } : {}),
      ...(source ? { source: truncate(source, 500) } : {}),
      appVersion: APP_VERSION,
      platform: platformTag(),
    };
    // eslint-disable-next-line no-console -- the console line is the on-device record; the POST is extra.
    console.error('[client-error]', report);
    void sink?.(report)?.catch(() => {
      /* swallow — a failed report POST (offline / 401 / network) must never surface */
    });
  } catch {
    /* defensive: reporting must never throw into the caller */
  }
}

let installed = false;

/**
 * Install process-wide handlers for failures that escape React: uncaught exceptions (`error`) and
 * unhandled promise rejections. Idempotent. React render errors are captured separately by ErrorBoundary.
 */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e: ErrorEvent) => {
    // Resource-load failures (img/script) also fire `error` on window with no `error` and an empty
    // message — those aren't app crashes, so skip them.
    if (!e.error && !e.message) return;
    reportClientError(
      'onerror',
      e.error ?? e.message,
      e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined,
    );
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    reportClientError('unhandledrejection', e.reason);
  });
}
